import { sample } from 'effector'

import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  $isPinnedDialogOpen,
  $pinnedMessagePages,
  $pinnedMessages,
  addPinnedMessageByFanId,
  getConcretePinnedMessagePageFx,
  getNextPinnedMessagePageFx,
  getPinnedMessagesFx,
  pinMessageFx,
  resetPinnedMessages,
  setPinnedMessageIdToFetch,
  unpinMessageFx,
} from '@/models/chattingUI/messages/pinnedMessages.ts'
import {
  resetSearchText,
  searchPinnedTextFx,
} from '@/models/chattingUI/messages/pinnedMessagesSearch.ts'
import { searchTextFx } from '@/models/chattingUI/messages/search.ts'
import {
  $chatterLanguage,
  $isAITranslatorEnabled,
  $isAutoTranslationsEnabled,
  $messageIdsToTranslate,
  $translationsByMessageIdMap,
  addMessageIdsToTranslate,
  translateMessagesFx,
} from '@/models/chattingUI/messages/translations.ts'

import {
  $messages,
  $messagesByFanIdMap,
  $messagesByIdMap,
  $messagesPagesByFanIdMap,
  addMessageByFanId,
  addMessageToMap,
  deleteMessageFx,
  getConcreteMessagePageFx,
  getNextMessagePageFx,
  getPreviousMessagePageFx,
  resetMessagesByFanId,
  setMessageIdToFetch,
  toggleMessageLikeFx,
} from './messages'

/**
 * Load messages only if no requested message exists in store.
 */
sample({
  clock: setMessageIdToFetch,
  source: { map: $messagesByFanIdMap, fanId: $fanId },
  filter: ({ map, fanId }, data) =>
    !!data &&
    !map[data.fanId ?? fanId]?.some(({ id }) => id === data.messageIdToFetch),
  fn: ({ fanId }, data) => ({
    fanId: data!.fanId ?? fanId,
    messageIdToFetch: data!.messageIdToFetch,
  }),
  target: getConcreteMessagePageFx,
})

/**
 * Reset previous messages if we need to jump to a concrete one.
 */
sample({
  clock: getConcreteMessagePageFx,
  fn: ({ fanId }) => ({ fanId }),
  target: resetMessagesByFanId,
})

sample({
  clock: [getNextMessagePageFx.doneData, getConcreteMessagePageFx.doneData],
  source: { store: $messagesPagesByFanIdMap },
  fn: ({ store }, page) => {
    return {
      ...store,
      [page.fanId]: store[page.fanId] ? [...store[page.fanId], page] : [page],
    }
  },
  target: $messagesPagesByFanIdMap,
})

sample({
  clock: getPreviousMessagePageFx.doneData,
  source: { store: $messagesPagesByFanIdMap },
  fn: ({ store }, page) => {
    return {
      ...store,
      [page.fanId]: store[page.fanId] ? [page, ...store[page.fanId]] : [page],
    }
  },
  target: $messagesPagesByFanIdMap,
})

/**
 * Load prev page if possible, make searched message `in the middle`.
 */
sample({
  clock: getConcreteMessagePageFx.doneData,
  fn: ({ list, hasMore }) => list.length > 0 && hasMore,
  target: getPreviousMessagePageFx,
})

/**
 * After search jump to the first message.
 */
sample({
  clock: searchTextFx.doneData,
  fn: ({ list }) => ({ messageIdToFetch: list[list.length - 1] }),
  target: setMessageIdToFetch,
})

sample({
  clock: setPinnedMessageIdToFetch,
  source: { messages: $pinnedMessages },
  filter: ({ messages }, messageIdToFetch) =>
    !!messageIdToFetch && !messages.some(({ id }) => id === messageIdToFetch),
  fn: (_, anchorMessageId) => anchorMessageId!,
  target: getConcretePinnedMessagePageFx,
})

/**
 * Reset previous messages if we need to jump to a concrete one.
 */
sample({
  clock: getConcretePinnedMessagePageFx,
  target: resetPinnedMessages,
})

sample({
  clock: [
    getNextPinnedMessagePageFx.doneData,
    getConcretePinnedMessagePageFx.doneData,
  ],
  source: { store: $pinnedMessagePages },
  fn: ({ store }, page) => [...store, page],
  target: $pinnedMessagePages,
})

/**
 * After search jump to the first message.
 */
sample({
  clock: searchPinnedTextFx.doneData,
  fn: ({ list }) => list[list.length - 1],
  target: setPinnedMessageIdToFetch,
})

sample({
  clock: [
    pinMessageFx.doneData,
    unpinMessageFx.doneData,
    toggleMessageLikeFx.doneData,
    deleteMessageFx.doneData,
  ],
  target: addMessageByFanId,
})

sample({
  clock: [pinMessageFx.doneData, toggleMessageLikeFx.doneData],
  fn: ({ message }) => message,
  target: addPinnedMessageByFanId,
})

getPinnedMessagesFx.doneData.watch(({ list }) => {
  list.forEach((message) => {
    addMessageToMap({ message })
  })
})

/**
 * Start messages translation.
 */
sample({
  clock: $messageIdsToTranslate,
  source: {
    isTranslating: translateMessagesFx.pending,
  },
  filter: ({ isTranslating }, ids) => !isTranslating && !!ids.length,
  fn: (_, ids) => ({ messageIds: ids.slice(0, 10) }),
  target: translateMessagesFx,
})

/**
 * Find what messages should be translated.
 */
sample({
  clock: [
    $messages,
    $pinnedMessages,
    $isAITranslatorEnabled,
    $isAutoTranslationsEnabled,
    $chatterLanguage,
  ],
  source: {
    messages: $messages,
    messagesMap: $messagesByIdMap,
    pinnedMessages: $pinnedMessages,
    isAITranslatorEnabled: $isAITranslatorEnabled,
    isAutoTranslationsEnabled: $isAutoTranslationsEnabled,
    chatterLanguage: $chatterLanguage,
    toTranslate: $messageIdsToTranslate,
    translationsMap: $translationsByMessageIdMap,
  },
  filter: ({
    messages,
    messagesMap,
    pinnedMessages,
    isAITranslatorEnabled,
    isAutoTranslationsEnabled,
    chatterLanguage,
    toTranslate,
    translationsMap,
  }) => {
    const basicCondition =
      !!chatterLanguage &&
      isAITranslatorEnabled &&
      isAutoTranslationsEnabled &&
      (messages.length || pinnedMessages.length)

    if (!basicCondition) {
      return false
    }

    return messages
      .filter(({ text, _isTempMessage }) => text && !_isTempMessage)
      .map(({ id }) => id)
      .concat(
        pinnedMessages
          .filter(({ id }) => messagesMap[id]?.text)
          .map(({ id }) => id),
      )
      .some((id) => {
        return (
          !toTranslate.includes(id) &&
          !translationsMap[id]?.[chatterLanguage!]?.isLoading &&
          !translationsMap[id]?.[chatterLanguage!]?.isError &&
          !translationsMap[id]?.[chatterLanguage!]?.text
        )
      })
  },
  fn: ({
    messages,
    messagesMap,
    pinnedMessages,
    chatterLanguage,
    toTranslate,
    translationsMap,
  }) =>
    messages
      .filter(({ text, _isTempMessage }) => text && !_isTempMessage)
      .map(({ id }) => id)
      .concat(
        pinnedMessages
          .filter(({ id }) => messagesMap[id]?.text)
          .map(({ id }) => id),
      )
      .filter((id) => {
        return (
          !toTranslate.includes(id) &&
          !translationsMap[id]?.[chatterLanguage!]?.isLoading &&
          !translationsMap[id]?.[chatterLanguage!]?.isError &&
          !translationsMap[id]?.[chatterLanguage!]?.text
        )
      }),
  target: addMessageIdsToTranslate,
})

/**
 * Reset pinned messages search on the dialog close
 */
sample({
  clock: $isPinnedDialogOpen,
  filter: (isPinnedDialogOpen) => !isPinnedDialogOpen,
  target: [resetSearchText],
})

/**
 * Load pinned messages when we open `Pinned messages dialog`.
 */
sample({
  clock: $isPinnedDialogOpen,
  filter: (isPinnedDialogOpen) => isPinnedDialogOpen,
  target: [resetPinnedMessages, getNextPinnedMessagePageFx],
})

/**
 * Reload pinned messages data when we delete any message.
 */
sample({
  clock: deleteMessageFx.doneData,
  source: { isPinnedDialogOpen: $isPinnedDialogOpen },
  filter: ({ isPinnedDialogOpen }) => isPinnedDialogOpen,
  target: [resetPinnedMessages, getNextPinnedMessagePageFx],
})
